/* ──────────────────────────────────────────────────────────
   theme.js — GeoGadgets Clean
   Tier-1 vanilla JS: cart drawer, variant switching, add-to-cart,
   slideshow, cart-page line-item update.

   IMPORTANT: variant logic mirrors variant-picker.liquid.
     - buyable = variant.available
     - sold-out gate = !variant.available
     - low-stock banner gated on inventory_management != null
       AND inventory_quantity != null AND 0 < qty <= threshold
   ────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  const routes = (window.theme && window.theme.routes) || {};
  const strings = (window.theme && window.theme.strings) || {};

  /* ── Helpers ───────────────────────────────────────────── */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function formatMoney(cents) {
    // Shopify-style AUD formatting. Storefront re-renders via /cart.js after each add.
    if (cents == null) return '';
    const dollars = (cents / 100).toFixed(2);
    return '$' + dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function fetchJSON(url, opts) {
    return fetch(url, Object.assign({ headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' } }, opts || {}))
      .then(function (r) {
        if (!r.ok) {
          return r.json().then(function (err) { throw err; });
        }
        return r.json();
      });
  }

  /* ── Cart drawer ───────────────────────────────────────── */
  const Drawer = {
    el: null,
    open: function () {
      if (!this.el) this.el = document.getElementById('CartDrawer');
      if (!this.el) return;
      this.el.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    },
    close: function () {
      if (!this.el) return;
      this.el.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    },
    refresh: function () {
      // Refetch the cart and rerender the drawer body.
      return fetchJSON((routes.cart_url || '/cart') + '.js').then(function (cart) {
        Drawer._render(cart);
        updateCartCount(cart.item_count);
        return cart;
      });
    },
    _render: function (cart) {
      if (!this.el) this.el = document.getElementById('CartDrawer');
      if (!this.el) return;
      const body = $('[data-cart-drawer-body]', this.el);
      const subtotalEls = $$('[data-cart-subtotal]', this.el);
      subtotalEls.forEach(function (e) { e.textContent = formatMoney(cart.total_price); });

      if (!cart.items || cart.items.length === 0) {
        body.innerHTML =
          '<div class="cart-drawer__empty">' +
          '<p>' + (strings.cartEmpty || 'Your cart is empty.') + '</p>' +
          '<a href="' + (routes.all_products_collection_url || '/collections/all') + '" class="btn-primary">Continue Shopping</a>' +
          '</div>';
        return;
      }

      const html = cart.items.map(function (item) {
        return (
          '<li class="cart-item" data-cart-item data-key="' + item.key + '">' +
          '  <a href="' + item.url + '" class="cart-item__image">' +
          (item.image ? '<img src="' + item.image + '" alt="" width="60" height="60">' : '') +
          '  </a>' +
          '  <div class="cart-item__details">' +
          '    <a href="' + item.url + '" class="cart-item__title">' + escapeHtml(item.product_title) + '</a>' +
          (item.variant_title && item.variant_title !== 'Default Title'
            ? '<div class="cart-item__variant">' + escapeHtml(item.variant_title) + '</div>'
            : '') +
          '    <div class="cart-item__price">' + formatMoney(item.final_line_price) + '</div>' +
          '    <div class="cart-item__qty">' +
          '      <button type="button" class="cart-item__qty-btn" data-cart-decrement>&minus;</button>' +
          '      <input type="number" class="cart-item__qty-input" data-cart-qty value="' + item.quantity + '" min="0">' +
          '      <button type="button" class="cart-item__qty-btn" data-cart-increment>&plus;</button>' +
          '      <button type="button" class="cart-item__remove" data-cart-remove>Remove</button>' +
          '    </div>' +
          '  </div>' +
          '</li>'
        );
      }).join('');
      body.innerHTML = '<ul class="cart-drawer__items" data-cart-drawer-items>' + html + '</ul>';
    }
  };

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function updateCartCount(n) {
    $$('[data-cart-count]').forEach(function (el) {
      el.textContent = n;
      el.setAttribute('data-cart-count', n);
    });
  }

  /* ── Add-to-cart form submit (PDP + cards) ─────────────── */
  function bindAddToCartForms() {
    document.addEventListener('submit', function (e) {
      const form = e.target.closest('.product-form');
      if (!form) return;
      if (form.matches('[data-cart-form]')) return; // cart page handles itself
      e.preventDefault();

      const submit = form.querySelector('[data-add-to-cart]');
      if (submit && submit.disabled) return;
      if (submit) submit.disabled = true;

      const formData = new FormData(form);
      fetch(routes.cart_add_url || '/cart/add', {
        method: 'POST',
        body: formData,
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
        .then(function (res) {
          if (!res.ok) {
            const msg = (res.body && (res.body.description || res.body.message)) || 'Could not add to cart.';
            alert(msg);
            return;
          }
          return Drawer.refresh().then(function () { Drawer.open(); });
        })
        .catch(function (err) {
          alert((err && (err.description || err.message)) || 'Could not add to cart.');
        })
        .then(function () {
          if (submit) submit.disabled = false;
        });
    });
  }

  /* ── Variant picker (PDP) ──────────────────────────────── */
  function bindVariantPickers() {
    $$('.product-form').forEach(function (form) {
      const variantsScript = form.querySelector('.product-form__variants-json');
      if (!variantsScript) return; // single-variant product, nothing to switch
      let variants;
      try { variants = JSON.parse(variantsScript.textContent); } catch (e) { return; }

      const selects = $$('.product-form__option-select', form);
      const idInput = form.querySelector('.product-form__variant-id');
      const submitBtn = form.querySelector('[data-add-to-cart]');
      const submitText = form.querySelector('[data-add-to-cart-text]');
      const priceWrap = form.querySelector('[data-product-price]');
      const stockBanner = form.querySelector('[data-stock-banner]');
      const lowStockThreshold = stockBanner ? parseInt(stockBanner.getAttribute('data-low-stock-threshold'), 10) || 10 : 10;

      function chosen() { return selects.map(function (s) { return s.value; }); }

      function findVariant(opts) {
        return variants.find(function (v) {
          return v.options.every(function (o, i) { return o === opts[i]; });
        });
      }

      function applyVariant(v) {
        if (!v) {
          if (submitBtn) {
            submitBtn.disabled = true;
            if (submitText) submitText.textContent = strings.unavailable || 'Unavailable';
          }
          return;
        }
        if (idInput) idInput.value = v.id;

        // Buyable gate = variant.available. NEVER check inventory_quantity here.
        const available = v.available === true;
        if (submitBtn) {
          submitBtn.disabled = !available;
          if (submitText) {
            submitText.textContent = available
              ? (strings.addToCart || 'Add to cart')
              : (strings.soldOut || 'Sold out');
          }
        }

        // Re-render price
        if (priceWrap) {
          const current = priceWrap.querySelector('[data-price-current]');
          const compare = priceWrap.querySelector('[data-price-compare]');
          if (current) current.textContent = formatMoney(v.price);
          if (compare) {
            if (v.compare_at_price && v.compare_at_price > v.price) {
              compare.textContent = formatMoney(v.compare_at_price);
              compare.hidden = false;
              priceWrap.querySelector('.price').classList.add('price--on-sale');
            } else {
              compare.hidden = true;
              priceWrap.querySelector('.price').classList.remove('price--on-sale');
            }
          }
        }

        // Stock banner gating — mirror variant-picker.liquid rules exactly.
        if (stockBanner) {
          const managed = v.inventory_management != null && v.inventory_management !== '';
          const qty = v.inventory_quantity;
          const show = managed && qty != null && qty > 0 && qty <= lowStockThreshold;
          if (show) {
            stockBanner.hidden = false;
            stockBanner.innerHTML = 'Only <strong>' + qty + '</strong> left in stock.';
          } else {
            stockBanner.hidden = true;
          }
        }

        // Update URL (?variant=...) without reload, so refresh/share keeps selection.
        if (history.replaceState && v.id) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', v.id);
          history.replaceState({}, '', url.toString());
        }
      }

      // ── Virtual-axes picker ─────────────────────────────────────────
      // Shopify caps a product at 3 real options, so FixDesk folds
      // Connectivity into Storage ("128GB + Cellular") and What's-Included
      // into Condition ("Grade B · Device Only"). The storefront picker
      // splits those back into SEPARATE button rows. Rules (per George):
      //   - only available values ever render — no greyed-out buttons
      //   - the Connectivity row renders only when the model actually has
      //     a Cellular option
      //   - availability cascades: a value shows when a purchasable
      //     variant matches it with EARLIER rows held at their selection,
      //     so every variant stays reachable
      const wrap = form.querySelector('[data-virtual-picker]');
      if (wrap) {
        const CELL_SUFFIX = ' + Cellular';
        const COND_SEP = ' · ';
        const optionNames = selects.map(function (s) { return s.getAttribute('data-option-name') || ''; });
        const lower = optionNames.map(function (n) { return n.toLowerCase().trim(); });
        const storageIdx = lower.indexOf('storage');
        const condIdx = lower.indexOf('condition');
        const colourNames = ['colour', 'color'];

        function decompose(v) {
          const d = {};
          v.options.forEach(function (o, i) {
            if (i === storageIdx) {
              if (o.slice(-CELL_SUFFIX.length) === CELL_SUFFIX) {
                d['Storage'] = o.slice(0, -CELL_SUFFIX.length);
                d['Connectivity'] = 'Cellular';
              } else {
                d['Storage'] = o;
                d['Connectivity'] = 'Wi-Fi';
              }
            } else if (i === condIdx) {
              const at = o.indexOf(COND_SEP);
              if (at !== -1) {
                d['Condition'] = o.slice(0, at);
                d["What's Included"] = o.slice(at + COND_SEP.length);
              } else {
                d['Condition'] = o;
              }
            } else {
              d[optionNames[i]] = o;
            }
          });
          return d;
        }

        // Virtual axis order: Storage, Connectivity, then the remaining
        // real axes (Colour, …), then Condition, What's Included.
        const axes = [];
        if (storageIdx !== -1) {
          axes.push('Storage');
          if (variants.some(function (v) { return decompose(v)['Connectivity'] === 'Cellular'; })) {
            axes.push('Connectivity');
          }
        }
        optionNames.forEach(function (n, i) {
          if (i !== storageIdx && i !== condIdx) axes.push(n);
        });
        if (condIdx !== -1) {
          axes.push('Condition');
          if (variants.some(function (v) { return decompose(v)["What's Included"] != null; })) {
            axes.push("What's Included");
          }
        }

        const decomposed = variants.map(function (v) { return { v: v, d: decompose(v) }; });

        function valueAvailable(axis, val, sel) {
          return decomposed.some(function (e) {
            if (e.v.available !== true) return false;
            if (e.d[axis] !== val) return false;
            for (let k = 0; k < axes.length; k++) {
              const b = axes[k];
              if (b === axis) break;
              if (e.d[b] !== undefined && e.d[b] !== sel[b]) return false;
            }
            return true;
          });
        }

        function axisValues(axis) {
          const seen = [];
          decomposed.forEach(function (e) {
            const val = e.d[axis];
            if (val != null && seen.indexOf(val) === -1) seen.push(val);
          });
          return seen;
        }

        function matchVariant(sel, mustBeAvailable) {
          const hit = decomposed.find(function (e) {
            if (mustBeAvailable && e.v.available !== true) return false;
            return axes.every(function (a) {
              return e.d[a] === undefined || e.d[a] === sel[a];
            });
          });
          return hit ? hit.v : null;
        }

        // Initial selection = current hidden-select state, else first available.
        let sel = {};
        const initial = findVariant(chosen()) || variants.find(function (v) { return v.available === true; }) || variants[0];
        if (initial) sel = decompose(initial);

        function cascadeFix() {
          axes.forEach(function (axis) {
            if (sel[axis] !== undefined && valueAvailable(axis, sel[axis], sel)) return;
            const firstOk = axisValues(axis).find(function (val) { return valueAvailable(axis, val, sel); });
            if (firstOk !== undefined) sel[axis] = firstOk;
          });
        }

        // Colour swatch mapping lives here (JS), not in Liquid — the old
        // Liquid map silently failed and rendered every dot #999999 grey.
        const SWATCH_HEX = {
          'black': '#1c1c1c', 'phantom black': '#1a1a1c', 'graphite black': '#2a2a2a',
          'white': '#f2f2f0', 'phantom white': '#ece9e2',
          'graphite': '#3a3a3a', 'space gray': '#54585c', 'space grey': '#54585c',
          'grey': '#7a7d80', 'gray': '#7a7d80',
          'silver': '#cfd2d6', 'mystic silver': '#cfd2d6',
          'gold': '#e3c9a0', 'rose gold': '#e8c2b3',
          'bronze': '#b08d6e', 'mystic bronze': '#b08d6e',
          'starlight': '#f0ead6', 'midnight': '#1f2233',
          'blue': '#235aa3', 'mystic blue': '#235aa3', 'navy': '#1d2b4a',
          'red': '#c8302a', 'green': '#4a8b5a', 'yellow': '#f3c92a',
          'purple': '#7e5fbf', 'pink': '#e89ab0', 'orange': '#e58438',
          'titanium': '#7a8085', 'natural titanium': '#8a8480', 'white titanium': '#d6cfc6',
          'blue titanium': '#5d6f7d', 'black titanium': '#2f3133', 'desert titanium': '#aa9173',
          'brown': '#8a6948', 'beige': '#d8c8a8', 'cream': '#f0e6d3', 'charcoal': '#36383b',
          'sage': '#94a78d', 'mint': '#9ed8c4', 'coral': '#f08f7a', 'lavender': '#c3b0e0',
        };
        function swatchHex(value) {
          const v = String(value || '').toLowerCase().trim();
          if (SWATCH_HEX[v]) return SWATCH_HEX[v];
          const fallbacks = [['titanium', '#7a8085'], ['black', '#1c1c1c'], ['white', '#f2f2f0'],
            ['blue', '#235aa3'], ['green', '#4a8b5a'], ['red', '#c8302a'], ['rose', '#e8c2b3'],
            ['gold', '#e3c9a0'], ['silver', '#cfd2d6'], ['pink', '#e89ab0'], ['purple', '#7e5fbf'],
            ['bronze', '#b08d6e'], ['grey', '#7a7d80'], ['gray', '#7a7d80']];
          for (let i = 0; i < fallbacks.length; i++) {
            if (v.indexOf(fallbacks[i][0]) !== -1) return fallbacks[i][1];
          }
          return '#999999';
        }
        function swatchMarkup(value) {
          const span = document.createElement('span');
          span.className = 'swatch';
          span.style.background = swatchHex(value);
          span.title = value;
          span.setAttribute('aria-label', value);
          return span.outerHTML;
        }

        function render() {
          wrap.innerHTML = '';
          axes.forEach(function (axis) {
            const values = axisValues(axis).filter(function (val) { return valueAvailable(axis, val, sel); });
            if (!values.length) return;
            // George's rule: no Connectivity row unless Cellular is on offer
            // for the current selection path (all-Wi-Fi = say nothing).
            if (axis === 'Connectivity' && values.length === 1 && values[0] === 'Wi-Fi') return;

            const isColour = colourNames.indexOf(axis.toLowerCase()) !== -1;
            const row = document.createElement('div');
            row.className = 'product-form__option product-form__option--' + (isColour ? 'swatch' : 'chip');
            const name = document.createElement('div');
            name.className = 'product-form__option-name';
            name.textContent = axis + ' ';
            const selectedLabel = document.createElement('span');
            selectedLabel.className = 'product-form__option-selected';
            selectedLabel.textContent = sel[axis] || '';
            name.appendChild(selectedLabel);
            row.appendChild(name);

            const list = document.createElement('div');
            list.className = 'product-form__option-row';
            list.setAttribute('role', 'radiogroup');
            list.setAttribute('aria-label', axis);
            values.forEach(function (val) {
              const btn = document.createElement('button');
              btn.type = 'button';
              const active = sel[axis] === val;
              const sw = isColour ? swatchMarkup(val) : null;
              btn.className = (sw ? 'swatch-chip' : 'option-chip') + (active ? ' active' : '');
              btn.setAttribute('data-option-value', val);
              btn.setAttribute('role', 'radio');
              btn.setAttribute('aria-checked', active ? 'true' : 'false');
              btn.setAttribute('aria-label', val);
              if (sw) { btn.innerHTML = sw; btn.title = val; } else { btn.textContent = val; }
              btn.addEventListener('click', function () {
                sel[axis] = val;
                cascadeFix();
                syncAndApply();
                render();
              });
              list.appendChild(btn);
            });
            row.appendChild(list);
            wrap.appendChild(row);
          });
        }

        function syncAndApply() {
          const v = matchVariant(sel, true) || matchVariant(sel, false);
          if (v) {
            selects.forEach(function (s, i) { s.value = v.options[i]; });
          }
          applyVariant(v);
        }

        cascadeFix();
        syncAndApply();
        render();
      } else {
        // Legacy fallback (no virtual container in the markup).
        selects.forEach(function (sel) {
          sel.addEventListener('change', function () {
            applyVariant(findVariant(chosen()));
          });
        });
      }
    });
  }

  /* ── PDP thumbnail gallery ─────────────────────────────── */
  function bindThumbs() {
    $$('[data-product-thumbs]').forEach(function (wrap) {
      const main = $('[data-product-main-image]');
      wrap.addEventListener('click', function (e) {
        const thumb = e.target.closest('[data-thumb]');
        if (!thumb || !main) return;
        e.preventDefault();
        main.src = thumb.getAttribute('data-full-src');
        $$('[data-thumb]', wrap).forEach(function (t) { t.classList.remove('is-active'); });
        thumb.classList.add('is-active');
      });
    });
  }

  /* ── Cart drawer open/close events ─────────────────────── */
  function bindDrawerEvents() {
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-cart-drawer-close]')) {
        e.preventDefault();
        Drawer.close();
      }
      const cartLink = e.target.closest('[data-cart-link]');
      if (cartLink) {
        e.preventDefault();
        Drawer.refresh().then(function () { Drawer.open(); });
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') Drawer.close();
    });
  }

  /* ── Cart drawer + cart-page line item handling ───────── */
  function bindCartLineEvents() {
    document.addEventListener('click', function (e) {
      const item = e.target.closest('[data-cart-item]');
      if (!item) return;
      const key = item.getAttribute('data-key');

      if (e.target.closest('[data-cart-remove]')) {
        e.preventDefault();
        changeLine(key, 0);
      } else if (e.target.closest('[data-cart-increment]')) {
        e.preventDefault();
        const input = $('[data-cart-qty]', item);
        const next = (parseInt(input.value, 10) || 0) + 1;
        changeLine(key, next);
      } else if (e.target.closest('[data-cart-decrement]')) {
        e.preventDefault();
        const input = $('[data-cart-qty]', item);
        const next = Math.max(0, (parseInt(input.value, 10) || 0) - 1);
        changeLine(key, next);
      }
    });

    document.addEventListener('change', function (e) {
      const input = e.target.closest('[data-cart-qty]');
      if (!input) return;
      const item = input.closest('[data-cart-item]');
      const key = item.getAttribute('data-key');
      const next = Math.max(0, parseInt(input.value, 10) || 0);
      changeLine(key, next);
    });
  }

  function changeLine(key, quantity) {
    return fetch((routes.cart_change_url || '/cart/change') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        Drawer._render(cart);
        updateCartCount(cart.item_count);
        // If we're on the full cart page, refresh it.
        if (document.body.classList.contains('template-cart')) {
          window.location.reload();
        }
      })
      .catch(function () { /* swallow */ });
  }

  /* ── Slideshow ─────────────────────────────────────────── */
  function bindSlideshow() {
    $$('[data-slideshow]').forEach(function (slideshow) {
      const slides = $$('.hero-slide', slideshow);
      const dots = $$('[data-dot]', slideshow);
      if (slides.length <= 1) return;
      let idx = 0;
      const autoplay = slideshow.getAttribute('data-autoplay') === 'true';
      const interval = (parseInt(slideshow.getAttribute('data-interval'), 10) || 6) * 1000;

      function go(i) {
        idx = (i + slides.length) % slides.length;
        slides.forEach(function (s, n) { s.classList.toggle('hero-slide--active', n === idx); });
        dots.forEach(function (d, n) { d.classList.toggle('hero-dot--active', n === idx); });
      }
      dots.forEach(function (d, n) { d.addEventListener('click', function () { go(n); }); });
      if (autoplay) setInterval(function () { go(idx + 1); }, interval);
    });
  }

  /* ── Sort-by submit ────────────────────────────────────── */
  function bindSortBy() {
    $$('[data-sort-by-select]').forEach(function (sel) {
      sel.addEventListener('change', function () {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', sel.value);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      });
    });
  }

  /* ── Init ──────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', function () {
    bindAddToCartForms();
    bindVariantPickers();
    bindThumbs();
    bindDrawerEvents();
    bindCartLineEvents();
    bindSlideshow();
    bindSortBy();
    updateCartCount(window.theme.cartCount || 0);
  });

  // Expose for debugging.
  window.theme.Drawer = Drawer;
})();

/* ──────────────────────────────────────────────────────────
   Product card hover image cycle.
   On collection pages, hovering a product-card image cycles
   through the featured images of each unique colour variant
   at 800ms intervals. Hovering an individual swatch swaps the
   main image to that colour's variant image and pauses the
   cycle. Mouse-leave on the card restores the default image.
   No-op for cards with fewer than 2 colour variants.
   ────────────────────────────────────────────────────────── */
(function () {
  'use strict';
  const CYCLE_MS = 800;

  function initCard(card) {
    const img = card.querySelector('.product-img img');
    if (!img) return;
    const raw = card.getAttribute('data-variant-images') || '';
    const variants = raw.split('~~').filter(Boolean).map(function (rec) {
      const parts = rec.split('||');
      return { colour: parts[0], url: parts[1], avail: parts[2] === 'true' };
    }).filter(function (v) { return v.url; });
    // Hover-cycle only makes sense with 2+ distinct colour images.
    if (variants.length < 2) return;

    const defaultSrc = img.getAttribute('data-default-src') || img.src;
    let cycleTimer = null;
    let cycleIndex = 0;
    let swatchHoverActive = false;

    function startCycle() {
      stopCycle();
      cycleIndex = 0;
      cycleTimer = setInterval(function () {
        if (swatchHoverActive) return;
        img.src = variants[cycleIndex].url;
        cycleIndex = (cycleIndex + 1) % variants.length;
      }, CYCLE_MS);
    }
    function stopCycle() {
      if (cycleTimer) { clearInterval(cycleTimer); cycleTimer = null; }
      img.src = defaultSrc;
    }

    const imageLink = card.querySelector('.product-card__image-link');
    if (imageLink) {
      imageLink.addEventListener('mouseenter', startCycle);
      imageLink.addEventListener('mouseleave', stopCycle);
    }

    // Swatch hover takes precedence over the cycle.
    const swatches = card.querySelectorAll('.swatch[data-variant-image-url]');
    swatches.forEach(function (sw) {
      const swUrl = sw.getAttribute('data-variant-image-url');
      if (!swUrl) return;
      sw.addEventListener('mouseenter', function () {
        swatchHoverActive = true;
        img.src = swUrl;
      });
      sw.addEventListener('mouseleave', function () {
        swatchHoverActive = false;
        // If the card itself is still hovered, the running cycle interval
        // resumes on its next tick. Otherwise the imageLink mouseleave
        // already restored the default.
      });
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.product-card').forEach(initCard);
  });
})();

/* ──────────────────────────────────────────────────────────
   Rail-mode mega menu switcher (Option F).
   Each .submenu--rail has a column of .rail-item buttons and
   a stack of .submenu__pane elements. Hovering or clicking a
   rail-item makes its matching pane active and deactivates
   siblings. Match is by data-pane-id ↔ pane.id.
   ────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  function initRail(rail) {
    var items = rail.querySelectorAll('.rail-item');
    var panes = rail.querySelectorAll(':scope > .submenu__pane');
    if (!items.length || !panes.length) return;

    function activate(targetId) {
      items.forEach(function (btn) {
        var isActive = btn.getAttribute('data-pane-id') === targetId;
        btn.classList.toggle('rail-item--active', isActive);
        btn.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      });
      panes.forEach(function (pane) {
        pane.classList.toggle('submenu__pane--active', pane.id === targetId);
      });
    }

    items.forEach(function (btn) {
      var targetId = btn.getAttribute('data-pane-id');
      if (!targetId) return;
      btn.addEventListener('mouseenter', function () { activate(targetId); });
      btn.addEventListener('focus', function () { activate(targetId); });
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        activate(targetId);
      });
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.submenu--rail').forEach(initRail);
  });
})();

/* ──────────────────────────────────────────────────────────
   Showcase slider (wide hero).
   Toggles `.is-active` on slides + dots, advances on interval,
   pauses on hover when configured. Prev/Next arrows bound.
   No-op when the slider has 0 or 1 slides.
   ────────────────────────────────────────────────────────── */
(function() {
  function initShowcase(root) {
    var slides = root.querySelectorAll('[data-showcase-slide]');
    if (slides.length < 2) return; // nothing to rotate

    var dots = root.querySelectorAll('[data-showcase-dot]');
    var prev = root.querySelector('[data-showcase-prev]');
    var next = root.querySelector('[data-showcase-next]');
    var intervalMs = parseInt(root.getAttribute('data-auto-rotate'), 10) || 6000;
    var pauseOnHover = root.getAttribute('data-pause-on-hover') !== 'false';
    var index = 0;
    var timer = null;
    var paused = false;

    function show(i) {
      slides.forEach(function (s, idx) { s.classList.toggle('is-active', idx === i); });
      dots.forEach(function (d, idx) { d.classList.toggle('is-active', idx === i); });
      index = i;
    }
    function nextSlide() { show((index + 1) % slides.length); }
    function prevSlide() { show((index - 1 + slides.length) % slides.length); }

    function start() { if (!timer) timer = setInterval(function () { if (!paused) nextSlide(); }, intervalMs); }
    function stop()  { if (timer) { clearInterval(timer); timer = null; } }

    if (prev) prev.addEventListener('click', function () { prevSlide(); stop(); start(); });
    if (next) next.addEventListener('click', function () { nextSlide(); stop(); start(); });
    dots.forEach(function (d) {
      d.addEventListener('click', function () {
        show(parseInt(d.getAttribute('data-showcase-dot'), 10));
        stop(); start();
      });
    });

    if (pauseOnHover) {
      root.addEventListener('mouseenter', function () { paused = true; });
      root.addEventListener('mouseleave', function () { paused = false; });
    }

    start();
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.showcase-slider').forEach(initShowcase);
  });
})();
